<template>
  <div class="flex flex-col gap-5">
    <RfSimpleCard class="text-black-1 px-10! py-8! text-sm!">
      When authenticated users visit your site, Recurly Engage needs a User ID to customize prompts,
      actions, and downstream reporting. You may need your developers' help to complete this form to
      setup Recurly Engage fully. To improve security around User ID spoofing, we also recommend
      setting up JWT signing. For technical details or advanced usage, view the
      <a
        href="https://docs.recurly.com/recurly-engage/docs/custom-js-snippet"
        target="_blank"
        class="underline!"
        >Custom JS Snippet docs</a
      >.
      <div class="mt-4">
        <p class="h-6">
          <span class="mr-1">User ID Matching is</span>
          <span v-if="props.app.flags?.user_id_active">
            <b>Active <span class="promo-status-light green-color ml-1"></span></b>
            <code
              class="bg-goal ml-4 inline-block max-w-120 truncate rounded px-2 py-1 align-middle text-xs [direction:rtl]"
            >
              {{ props.app.flags.user_id_active }}
            </code>
            <v-icon
              size="16"
              class="ml-2 cursor-pointer align-middle opacity-40"
              @click="
                copyToClipboard(props.app.flags.user_id_active, 'User ID copied to clipboard.')
              "
              >md:content_copy</v-icon
            >
          </span>
          <span v-else>
            <b>Inactive <span class="promo-status-light ml-1"></span></b>
          </span>
          <v-tooltip location="right">
            <template #activator="{ props: tooltipProps }">
              <v-icon
                v-bind="tooltipProps"
                size="16"
                class="ml-2 opacity-40"
                @click="resetActiveFlag(props.app.flags?.user_id_active ? 'user_id' : null)"
                >md:refresh</v-icon
              >
            </template>
            <span>{{
              props.app.flags?.user_id_active
                ? "Clear the detected User ID. It will be refreshed after the next visitor session"
                : "Check if a User ID has been detected"
            }}</span>
          </v-tooltip>
        </p>
      </div>
      <div class="mt-2">
        <p class="h-6">
          <span class="mr-1">User JWT signing is</span>
          <span v-if="props.app.flags?.user_jwt_active">
            <b>
              {{ isJwtValid ? "Active" : "Invalid" }}
              <span
                class="promo-status-light ml-1"
                :class="isJwtValid ? 'green-color' : 'red-color'"
              ></span
            ></b>
            <code
              class="bg-goal ml-4 inline-block max-w-120 truncate rounded px-2 py-1 align-middle text-xs [direction:rtl]"
            >
              {{ props.app.flags.user_jwt_active }}
            </code>
            <v-icon
              size="16"
              class="ml-2 cursor-pointer align-middle opacity-40"
              @click="
                copyToClipboard(props.app.flags.user_jwt_active, 'User JWT copied to clipboard.')
              "
              >md:content_copy</v-icon
            >
          </span>
          <span v-else>
            <b>Inactive <span class="promo-status-light ml-1"></span></b>
          </span>
          <v-tooltip location="right">
            <template #activator="{ props: tooltipProps }">
              <v-icon
                v-bind="tooltipProps"
                size="16"
                class="ml-2 opacity-40"
                @click="resetActiveFlag(props.app.flags?.user_jwt_active ? 'user_jwt' : null)"
                >md:refresh</v-icon
              >
            </template>
            <span>{{
              props.app.flags?.user_jwt_active
                ? "Clear the detected User JWT. It will be refreshed after the next visitor session"
                : "Check if a User JWT has been detected"
            }}</span>
          </v-tooltip>
        </p>
      </div>
      <div v-if="props.app.providers?.user_id_jwt?.secret" class="mt-4">
        <RfInput
          :modelValue="props.app.providers.user_id_jwt.secret"
          class="w-full"
          disabled
          variant="outlined"
          hintMessage="Use this secret to implement JWT signing on your server. The JWT should be accessible by the fetchUserJwt function."
        >
          <template #append>
            <span class="inline-flex items-center gap-4">
              <button
                type="button"
                class="relative inline-flex shrink-0 place-items-center before:absolute before:-inset-3 before:cursor-pointer"
                @click="
                  copyToClipboard(
                    props.app.providers.user_id_jwt.secret,
                    'Secret copied to clipboard.',
                  )
                "
              >
                <RfCopyIcon class="h-6! w-6!" />
              </button>
              <button
                type="button"
                class="relative inline-flex shrink-0 place-items-center before:absolute before:-inset-3 before:cursor-pointer"
                @click="showRegenerateDialog = true"
              >
                <RfResetIcon class="fill-beige-50 h-6! w-6!" />
              </button>
            </span>
          </template>
        </RfInput>
      </div>
    </RfSimpleCard>
    <RfSimpleCard class="text-black-1 rounded-b-none! border-b-0! p-10 text-sm">
      <div
        class="[&_.rf-text-input--title]:text-black-2 flex flex-col gap-5 [&_.rf-text-input--title]:text-sm [&_.rf-text-input--title]:font-bold"
      >
        <div class="rf-settings-section-title"><h4>Authenticated User ID Matching</h4></div>
        <div>
          Recurly Engage can directly access your authenticated visitors&apos; User ID if the
          following store location and key is specified. If the resulting value is encoded, you can
          specify how it should be decoded. Finally, if the resulting value is a JSON string, it
          will automatically be parsed, and you can specify a path to access the desired property.
        </div>
        <RfRadioGroup
          v-model="settingsForm.userid_store_type"
          legend="Fetch from"
          :options="[
            { value: 'localstorage', label: 'localStorage' },
            { value: 'sessionstorage', label: 'sessionStorage' },
            { value: 'cookie', label: 'cookie' },
            { value: 'window', label: 'window' },
            { value: '', label: 'Custom JS Snippet', separate: true },
          ]"
          :disabled="userStore.isDisabledWithMessage"
        />
        <RfInput
          v-model.trim="settingsForm.userid_store_key"
          variant="outlined"
          title="Store key"
          placeholder="e.g. ajs_user_id"
          :disabled="useJSSnippet || userStore.isDisabledWithMessage"
        />
        <RfRadioGroup
          v-model="settingsForm.userid_decode"
          legend="Decode using"
          :options="[
            { value: '', label: 'none' },
            { value: 'base64', label: 'Base64' },
            { value: 'jwt', label: 'JWT' },
          ]"
          :disabled="useJSSnippet || userStore.isDisabledWithMessage"
        />
        <RfInput
          v-model.trim="settingsForm.userid_prop_path"
          variant="outlined"
          title="Property path"
          placeholder="e.g. auth.user.id"
          :disabled="useJSSnippet || userStore.isDisabledWithMessage"
        />
        <div class="rf-settings-section-title my-5"><h4>Test Account Credentials</h4></div>
        <div>
          <p>
            Providing login details for a test account on your site will help expedite the setup
            process. With this, we can help verify if the User ID is matched properly, troubleshoot,
            or suggest possible values for the above form.
          </p>
          <br />
          <p>
            <strong>Note:</strong> these credentials are visible to Recurly employees and all users
            in your organization. Do ensure that the test account contains no sensitive information
            and cannot perform any potentially damaging actions.
          </p>
        </div>
        <RfInput
          v-model.trim="providerForm.url"
          variant="outlined"
          title="Login Page URL"
          placeholder="e.g. http://your-site.com/login"
          :disabled="userStore.isDisabledWithMessage"
        />
        <RfInput
          v-model.trim="providerForm.login"
          variant="outlined"
          title="Username / Email"
          placeholder="e.g. tester@your-site.com"
          :disabled="userStore.isDisabledWithMessage"
        />
        <RfInput
          v-model.trim="providerForm.plaintext_password"
          variant="outlined"
          title="Password"
          placeholder="e.g. abc123"
          :disabled="userStore.isDisabledWithMessage"
        />
      </div>
    </RfSimpleCard>
  </div>

  <v-dialog v-model="showRegenerateDialog" width="640" persistent>
    <v-card class="rf-custom-web-card-wrapper">
      <v-card-title>Regenerate JWT Secret?</v-card-title>
      <v-card-text>
        Are you sure you want to regenerate the JWT signing secret? Any JWTs signed with the current
        secret will immediately become invalid.
      </v-card-text>
      <v-card-actions class="modal-card-actions-div">
        <v-btn
          size="large"
          class="cancel-btn"
          variant="outlined"
          @click="showRegenerateDialog = false"
          >Cancel</v-btn
        >
        <v-btn
          size="large"
          style="width: 200px"
          variant="flat"
          color="success"
          @click="
            appsStore.generateJwtSecret(props.app.id);
            showRegenerateDialog = false;
          "
          >Regenerate Secret</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import { useUserStore } from "@/pinia/userStore";
import { useAppsStore } from "@/pinia/appsStore";
import { computed, onMounted, reactive, ref, watch } from "vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfRadioGroup from "../inputs/RfRadioGroup.vue";
import RfCopyIcon from "@/components/icons/RfCopyIcon.vue";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import copyToClipboard from "@/utils/CopyToClipboard";

const props = defineProps(["app"]);
const userStore = useUserStore();
const appsStore = useAppsStore();

const settingsForm = reactive({
  userid_store_type: "",
  userid_decode: "",
  ...props.app.web_snippet_settings,
});
const providerForm = reactive({ ...props.app.providers.test_account });

const useJSSnippet = computed(() => !settingsForm.userid_store_type);

const isJwtValid = ref(null);

const verifyJwt = async jwt => {
  if (!jwt) return null;
  try {
    const secret = props.app.providers.user_id_jwt.secret;
    const [header, payload, sig] = jwt.split(".");
    const key = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["verify"],
    );
    const base64 = sig.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const signature = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
    return await crypto.subtle.verify(
      "HMAC",
      key,
      signature,
      new TextEncoder().encode(`${header}.${payload}`),
    );
  } catch {
    return false;
  }
};

watch(
  () => props.app.flags?.user_jwt_active,
  async jwt => (isJwtValid.value = await verifyJwt(jwt)),
  { immediate: true },
);

const showRegenerateDialog = ref(false);

const resetActiveFlag = key => appsStore.getSdkActiveFlags(props.app.id, key);

const fillModel = model => {
  model.web_snippet_settings = { ...model.web_snippet_settings, ...settingsForm };
  model.providers.test_account = { ...model.providers.test_account, ...providerForm };
  const { web_snippet_settings } = model;
  if (!web_snippet_settings.userid_store_type) delete web_snippet_settings.userid_store_type;
  if (!web_snippet_settings.userid_decode) delete web_snippet_settings.userid_decode;
  return model;
};

onMounted(() => {
  const { flags } = props.app;
  if (!flags.user_id_active || !flags.user_jwt_active) {
    appsStore.getSdkActiveFlags(props.app.id);
  }
});

defineExpose({ fillModel });
</script>
